import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import FlagModalContent from 'components/modals/FlagModalContent';
import spal from 'components/modals/SpateAlert';
import { DATA_SOURCES, FLAG_STATUSES } from '@/config/constants';
import { apiDataSource } from 'utils/entities';
import { endpointWrapper } from 'utils/api/meta.api';
import { fetchEntities } from 'utils/api/entities.api';
import { getHierarchy } from 'utils/hooks/useGetHierarchy';
import { modals } from '@/copy';
import useCurrentCountry from './useCurrentCountry';

const { flagModals } = modals;
const { PENDING, CLOSED } = FLAG_STATUSES;

const FLAGS_KEY = 'flags';

/** The ids the current user has flagged. */
export const useFlags = () =>
    useQuery(FLAGS_KEY, () => endpointWrapper({ domain: 'flags' }), {
        staleTime: Infinity,
        placeholderData: [],
    });

/** Ids whose entity carries a name worth putting on the flag. */
const NAMED_ID_TYPES = [
    'categoryId',
    'brandId',
    'affinityId',
    'productId',
    'keywordId',
];

// Seeded entities expire after 5 minutes, so a drawer left open loses the name.
const fetchAffinityName = (id) =>
    fetchEntities({
        dataSource: apiDataSource(DATA_SOURCES.GOOGLE),
        entityType: 'affinities',
        ids: [id],
        minimal: true,
    })
        .then((entities) => entities?.[id]?.name)
        .catch(() => undefined);

/**
 * Add the names behind the ids, so data ops don't resolve a payload of bare
 * UUIDs by hand. Best-effort, from the entity cache the sidecar seeds: present
 * whenever the page that raised the flag had fetched the entity, which is true
 * of any row a user can see and click.
 */
const useEnrichFlagData = () => {
    const queryClient = useQueryClient();
    const country = useCurrentCountry();
    const session = useSelector(({ session }) => session);

    return useCallback(
        async (data) => {
            // Any source's cache will do: a name is a name whichever taxonomy
            // answered, so this matches on id across `entityQueryKey`s.
            const cachedEntity = (id) => {
                const match = queryClient
                    .getQueriesData(['entity'])
                    .find(([key, entity]) => entity && key?.[1]?.id === id);
                return match ? match[1] : null;
            };

            const enriched = Object.entries(data).reduce(
                (acc, [idType, id]) => {
                    if (country && country.id === id)
                        acc.countryName = country.name;
                    if (idType === 'hashtag') acc.hashtag = id;
                    if (!NAMED_ID_TYPES.includes(idType)) return acc;

                    const { hierarchies, name, kw } = cachedEntity(id) || {};
                    const { hierarchy_hint } = getHierarchy(hierarchies);

                    if (name) acc[`${idType.slice(0, -2)}Name`] = name;
                    if (hierarchy_hint) acc.hierarchy = hierarchy_hint;
                    if (kw) acc.keyword = kw;

                    return acc;
                },
                { ...data, userEmail: session?.user_email || '' },
            );

            if (enriched.affinityId && !enriched.affinityName) {
                const affinityName = await fetchAffinityName(
                    enriched.affinityId,
                );
                if (affinityName) enriched.affinityName = affinityName;
            }

            return enriched;
        },
        [queryClient, country, session],
    );
};

/**
 * Raise or close a flag. The list is updated in place rather than refetched:
 * the server returns nothing useful and the only change is one id.
 */
export const useSubmitFlag = () => {
    const queryClient = useQueryClient();
    const enrich = useEnrichFlagData();

    return useMutation(
        async ({ id, status, data }) =>
            endpointWrapper({
                domain: 'flags',
                data: { id, status, data: JSON.stringify(await enrich(data)) },
            }),
        {
            onSuccess: (_result, { id, status }) => {
                queryClient.setQueryData(FLAGS_KEY, (flags) => {
                    const current = flags || [];
                    if (status === PENDING)
                        return current.includes(id)
                            ? current
                            : [...current, id];
                    return current.filter((flagId) => flagId !== id);
                });
            },
        },
    );
};

const BREAK_PROMISE_CHAIN = 'BREAK_PROMISE_CHAIN';
const breakPromiseChain = () => Promise.reject(new Error(BREAK_PROMISE_CHAIN));
const maybeBreakPromiseChain = (err) => {
    if (err.message === BREAK_PROMISE_CHAIN) return;
    throw err;
};

/**
 * The flag flows, as imperative functions a click handler can call — they are
 * modal conversations rather than state transitions, so they are not mutations.
 */
export const useFlagActions = () => {
    const { mutateAsync: submitFlag } = useSubmitFlag();
    const country = useCurrentCountry();

    const unflagItem = useCallback(
        ({ flagId, data }) =>
            spal(flagModals.unflagModal.content, {
                dangerMode: true,
                buttons: {
                    cancel: flagModals.unflagModal.cancel,
                    confirm: {
                        text: flagModals.unflagModal.confirm,
                        closeModal: false,
                    },
                },
            })
                .then((undo) => {
                    if (!undo) return breakPromiseChain();
                    return submitFlag({ id: flagId, status: CLOSED, data });
                })
                .then(() =>
                    spal(flagModals.unflagSuccessMessage, { buttons: false }),
                )
                .catch(maybeBreakPromiseChain)
                .catch(() =>
                    spal(flagModals.errorMessage, {
                        buttons: false,
                        icon: 'error',
                    }),
                ),
        [submitFlag],
    );

    const flagItem = useCallback(
        ({ flagId, data }) =>
            spal(
                <div css="align-self: stretch;">
                    <FlagModalContent
                        onSubmit={(comment) =>
                            submitFlag({
                                id: flagId,
                                status: PENDING,
                                data: { ...data, comment },
                            })
                                .then(() =>
                                    unflagItem({
                                        flagId,
                                        data: { ...data, comment },
                                    }),
                                )
                                .catch(maybeBreakPromiseChain)
                                .catch(() =>
                                    spal(flagModals.errorMessage, {
                                        buttons: false,
                                        icon: 'error',
                                    }),
                                )
                        }
                    />
                </div>,
                { buttons: false },
            ),
        [submitFlag, unflagItem],
    );

    /** Submit, confirm, and offer a retry that re-runs the whole flow. */
    const submitWithRetry = useCallback(
        function run({ data, successMessage, errorMessage }) {
            return submitFlag({ id: uuid(), status: PENDING, data })
                .then(() =>
                    spal(successMessage, {
                        buttons: false,
                        title: flagModals.suggestTrend.title,
                        icon: 'success',
                    }),
                )
                .catch(() =>
                    spal(errorMessage, {
                        buttons: {
                            confirm: flagModals.suggestTrend.errorConfirm,
                        },
                        icon: 'error',
                    }),
                )
                .then((retry) => {
                    if (!retry) return breakPromiseChain();
                    return run({ data, successMessage, errorMessage });
                })
                .catch(maybeBreakPromiseChain);
        },
        [submitFlag],
    );

    const flagNoResultsQuery = useCallback(
        (noResultsQuery, comment) =>
            submitWithRetry({
                data: { noResultsQuery, comment, countryId: country?.id },
                successMessage:
                    flagModals.suggestTrend.createMessage(noResultsQuery),
                errorMessage: flagModals.errorMessage,
            }),
        [submitWithRetry, country],
    );

    const requestAnalysis = useCallback(
        (suggestion, comment) =>
            submitWithRetry({
                data: { suggestion, comment, countryId: country?.id },
                successMessage: `You requested analysis for "${suggestion}" by industry. Our team will review and any change will be reflected in the next data update.`,
                errorMessage: 'Unable to complete your requst.',
            }),
        [submitWithRetry, country],
    );

    const submitDescriptionFeedback = useCallback(
        (data) =>
            submitWithRetry({
                data,
                successMessage:
                    'You sent feedback for this description. Our team will review and any change will be reflected in the next data update.',
                errorMessage: 'Unable to complete your requst.',
            }),
        [submitWithRetry],
    );

    return {
        flagItem,
        unflagItem,
        flagNoResultsQuery,
        requestAnalysis,
        submitDescriptionFeedback,
    };
};
